import type { Channel } from './guide.js';

/**
 * Quando un programma passato si può rivedere (06-RIPRODUZIONE, catch-up).
 *
 * ## Perché è una regola e non un `if` dentro la guida
 *
 * Perché le condizioni sono quattro e nessuna è ovvia, e perché la risposta va data **prima** di
 * chiedere qualcosa al gateway: un OK su un programma che non si può rivedere deve dire subito
 * perché, non dopo tre secondi di attesa e un rifiuto. Il gateway resta l'autorità — può sempre
 * rifiutare — ma un'app che manda richieste che sa già condannate fa aspettare per niente.
 *
 * ## Le quattro condizioni
 *
 * 1. il canale ha l'archivio (`tv_archive` del livello compatibile);
 * 2. il programma è **finito**: uno in corso è la diretta, e mandarlo in catch-up darebbe un flusso
 *    che insegue sé stesso;
 * 3. non è più vecchio dei giorni di archivio che il canale dichiara;
 * 4. non è nel **futuro**: un programma che deve ancora andare in onda non esiste da nessuna parte,
 *    e chiederlo produce un errore che sembra un guasto invece di un'attesa.
 *
 * La terza è quella che si dimentica, e si dimentica in modo asimmetrico: `archiveHours` è un numero
 * che il fornitore dichiara e che nessuno verifica, quindi un canale può annunciare sette giorni e
 * averne due. Per questo il rifiuto del gateway resta possibile e va gestito: questa regola toglie
 * le richieste **certamente** inutili, non promette che le altre funzionino.
 */

export type CatchupVerdict =
  | { readonly ok: true }
  | {
      readonly ok: false;
      /** Perché no. Ognuno merita una frase diversa: sono situazioni diverse per chi guarda. */
      readonly reason: 'no-archive' | 'still-live' | 'too-old' | 'in-the-future';
    };

export interface CatchupRequest {
  readonly channel: Channel;
  readonly programStartMs: number;
  readonly programEndMs: number;
  readonly nowMs: number;
}

/**
 * Un margine sul confine dei giorni di archivio.
 *
 * Senza, un programma esattamente al limite è «dentro» o «fuori» a seconda del millisecondo in cui
 * si preme, e la stessa riga risponde in due modi a due secondi di distanza. Cinque minuti è meno
 * di un programma e più di qualunque disallineamento d'orologio fra apparecchio e server.
 */
export const ARCHIVE_EDGE_MS = 5 * 60 * 1000;

export function catchupVerdict(request: CatchupRequest): CatchupVerdict {
  const { channel, programStartMs, programEndMs, nowMs } = request;

  /*
    **Prima si guarda il programma, poi il canale.**

    L'archivio era il primo controllo, e faceva rispondere «non è in archivio» a un programma **in
    onda** su un canale senza archivio. La risposta era vera e fuori tema: chi preme OK sul programma
    in corso non sta chiedendo di rivederlo, sta chiedendo di guardarlo. A monte, la schermata
    trasformava quel rifiuto in un errore e riapriva l'elenco — cioè un canale perfettamente
    guardabile non si apriva dalla guida.

    Su stage non si vedeva perché nessun canale ha l'EPG e quindi non c'è nessuna cella da premere.
    Sarebbe comparso il giorno della mappatura, su **tutti** i canali senza `tv_archive` — la
    maggioranza — e sarebbe sembrato un guasto della mappatura.

    `in-the-future` e `still-live` sono fatti del programma; `no-archive` e `too-old` sono fatti del
    canale, e contano solo una volta stabilito che il programma è passato.
  */
  if (programStartMs > nowMs) return { ok: false, reason: 'in-the-future' };

  // In corso: è la diretta, e ci si arriva premendo OK sulla diretta, non sul catch-up.
  if (programEndMs > nowMs) return { ok: false, reason: 'still-live' };

  if (!channel.hasArchive || channel.archiveHours <= 0) return { ok: false, reason: 'no-archive' };

  const oldestMs = nowMs - channel.archiveHours * 60 * 60 * 1000 - ARCHIVE_EDGE_MS;
  if (programStartMs < oldestMs) return { ok: false, reason: 'too-old' };

  return { ok: true };
}

/**
 * Se si può **guardare dall'inizio** questo programma.
 *
 * ## È una domanda diversa da «si può rivedere»
 *
 * `catchupVerdict` risponde a «riproduci questa replica», e su un programma **in onda** dice
 * `still-live` — giustamente, perché non è una replica. Ma «guarda dall'inizio» ha senso proprio
 * lì: la partita è cominciata venti minuti fa e la si vuole dal fischio d'avvio.
 *
 * Quindi qui conta una cosa sola: **l'archivio copre l'istante d'inizio?** Non importa se il
 * programma sia finito o ancora in corso, perché il gesto è lo stesso — si chiede all'archivio di
 * ripartire da un istante che esiste.
 *
 * Un programma che deve ancora cominciare non si può guardare dall'inizio: quell'inizio non è
 * ancora accaduto, e l'archivio non registra il futuro.
 */
export function canWatchFromStart(
  channel: Channel,
  programStartMs: number,
  nowMs: number,
): boolean {
  if (!channel.hasArchive || channel.archiveHours <= 0) return false;
  if (programStartMs > nowMs) return false;
  const oldestMs = nowMs - channel.archiveHours * 60 * 60 * 1000 - ARCHIVE_EDGE_MS;
  return programStartMs >= oldestMs;
}
